import React, { useState, useMemo, useEffect, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { format, parseISO } from "date-fns";
import { Form, Col, Row, Dropdown, Button } from "react-bootstrap";
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import { FaCog, FaChevronDown } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import DateRangeFilter from "../components/DateRangeFilter";
import SelectFilter from "../components/SelectFilter";
import { debounce } from "lodash";
import useFetchData from "../components/useFetchDatav2";
import { useToast } from "../Context/useToast";
import { startOfMonth, endOfMonth } from "date-fns";

import PdfModal from "../components/PdfModal";
import DocumentModal from "../components/DocumentModal";
import { DOCUMENT_TYPES } from "../constants/documentTypes.js";
import { DOCUMENT_STATUS } from "../constants/documentStatus.js";
import { DOCUMENT_STEP } from "../constants/documentStep.js";
import { DOCUMENT_PROCESSING_STATUS } from "../constants/documentProcessingStatus.js";
import prettyBytes from "pretty-bytes";

const styles = `
  #dropdown-table-columns::after {
    display: none !important;
  }
  .dropdown-table-columns-width {
    width: 200px;
  }
  .hidden-input {
    opacity: 0;
  }
`;

const TableSettingDropdown = ({ showDropdown, handleToggleDropdown, tempPagination, handlePageSizeChange, table, tempColumnVisibility, handleColumnVisibilityChange, handleApplyChanges, handleCancelChanges }) => {
   return (
      <Dropdown show={showDropdown} onToggle={handleToggleDropdown}>
         <Dropdown.Toggle as="span" variant="link" id="dropdown-table-columns" style={{ cursor: "pointer" }}>
            <FaCog />
         </Dropdown.Toggle>
         <Dropdown.Menu className="dropdown-table-columns-width" onClick={(e) => e.stopPropagation()}>
            <Dropdown.Header>Rows</Dropdown.Header>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="10"
                  checked={tempPagination.pageSize === 10}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(10);
                  }}
               />
            </div>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="20"
                  checked={tempPagination.pageSize === 20}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(20);
                  }}
               />
            </div>
            <div className="dropdown-item">
               <Form.Check
                  type="checkbox"
                  label="50"
                  checked={tempPagination.pageSize === 50}
                  onChange={(e) => {
                     e.stopPropagation();
                     handlePageSizeChange(50);
                  }}
               />
            </div>
            <Dropdown.Divider />
            <Dropdown.Header>View Columns</Dropdown.Header>
            {table
               .getAllColumns()
               .filter((column) => !column.columnDef.excludeFromDropdown)
               .map((column) => (
                  <div className="dropdown-item" key={column.id} as="label">
                     <div className="form-check">
                        <input
                           type="checkbox"
                           className="form-check-input"
                           checked={tempColumnVisibility?.[column.id] ?? true}
                           onChange={(e) => {
                              e.stopPropagation();
                              handleColumnVisibilityChange(column.id);
                           }}
                           id={`column-visibility-${column.id}`}
                        />
                        <label className="form-check-label" htmlFor={`column-visibility-${column.id}`}>
                           {column.columnDef.meta?.label ?? column.id}
                        </label>
                     </div>
                  </div>
               ))}
            <Dropdown.Divider />
            <div className="d-flex justify-content-center p-2">
               <Button variant="secondary" size="sm" onClick={handleCancelChanges}>
                  Cancel
               </Button>
               <Button variant="primary" size="sm" onClick={handleApplyChanges} className="ms-2">
                  Apply
               </Button>
            </div>
         </Dropdown.Menu>
      </Dropdown>
   );
};
export default function Documents() {
   const [searchParams] = useSearchParams();
   const deepLinkDocId = searchParams.get("doc"); // doc_external_id from ?doc=<uuid>
   const deepLinkTriggeredRef = useRef(false);

   const defaultDateRange = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: "selection",
   };
   const [isLoading, setIsLoading] = useState(true);
   const [showDocumentModal, setShowDocumentModal] = useState(false);
   const [showDetailModal, setShowDetailModal] = useState(false);
   const [error, setError] = useState(null);
   const [docId, setDocId] = useState(null);
   const [docName, setDocName] = useState(null);
   const [docObj, setDocObj] = useState(null);
   const { fetchData } = useFetchData();
   const [imageData, setImageData] = useState(null);
   const { triggerToast } = useToast();
   const [documents, setDocuments] = useState([]);
   const [columnFilters, setColumnFilters] = useState(
      // When a deep-link param is present, start with no date filter so the
      // target document is included regardless of when it was uploaded.
      deepLinkDocId ? {} : { doc_status_date: defaultDateRange }
   );
   const columnFiltersRef = useRef(columnFilters);
   columnFiltersRef.current = columnFilters;
   const abortControllerRef = useRef(null);
   const prevFetchParamsRef = useRef(null);
   const getDocumentTypeLabel = (value) => {
      if (!value) return "Unknown";
      const type = DOCUMENT_TYPES.find((docType) => docType.value === value.toString());
      return type ? type.label : "Unknown";
   };
   const getDocumentStatusLabel = (value) => {
      if (!value) return "Unknown";
      const status = DOCUMENT_STATUS.find((docStatus) => docStatus.value === value.toString());
      return status ? status.label : "Unknown";
   };
   const getDocumentStepLabel = (value) => {
      if (!value) return "Unknown";
      const step = DOCUMENT_STEP.find((docStep) => docStep.value === value.toString());
      return step ? step.label : "Unknown";
   };
   const columns = useMemo(
      () => [
         {
            accessorKey: "doc_id",
            header: "doc_id",
            enableHiding: false, // Prevent visibility toggling
            enableColumnFilter: false, // Prevent filtering
            isVisible: false, // Always hidden
            excludeFromDropdown: true,
         },
         {
            accessorKey: "doc_name",
            accessorId: "doc_name",
            filter_name: "name",
            meta: { label: "Document Name" },
            header: () => <span>Document Name</span>,
            enableColumnFilter: true,
            manualFiltering: true,
            isVisible: true,
            cell: ({ row }) => (
               <a
                  href="#"
                  onClick={(e) => {
                     e.preventDefault();
                     handleOpenDocumentDetail(row.original.doc_id);
                  }}
                  style={{ cursor: "pointer", color: "#0073bb", textDecoration: "none" }}
               >
                  {row.original.doc_name}
               </a>
            ),
         },
         {
            accessorKey: "doc_type",
            accessorId: "doc_type",
            filter_name: "type",
            meta: { label: "Document Type" },
            header: () => <span>Document Type</span>,
            //Filter: ({ column }) => <SelectFilter column={{ ...column, value: columnFiltersRef.current[column.id] || "" }} constants={DOCUMENT_TYPES} handleInputFilterChangeNoDebounce={handleInputFilterChangeNoDebounce} />,
            Filter: ({ column }) => <SelectFilter column={{ ...column, value: columnFiltersRef.current[column.id] || "" }} constants={DOCUMENT_TYPES.slice(0, 2)} handleInputFilterChangeNoDebounce={handleInputFilterChangeNoDebounce} />,
            enableColumnFilter: true,
            isVisible: true,
            cell: ({ getValue }) => {
               const typ = getValue();
               return getDocumentTypeLabel(typ);
            },
         },
         {
            accessorKey: "doc_status_date",
            filter_name: "date",
            meta: { label: "Status Date" },
            header: () => <span>Status Date</span>,
            Filter: ({ column }) => <DateRangeFilter column={{ ...column, value: columnFiltersRef.current[column.id] || "" }} handleInputFilterChangeNoDebounce={handleInputFilterChangeNoDebounce} />,
            enableColumnFilter: true,
            isVisible: true,
            cell: ({ getValue }) => {
               const dat = getValue();
               try {
                  if (!dat) {
                     return "N/A";
                  }
                  const parsedDate = parseISO(dat);
                  return format(parsedDate, "yyyy-MM-dd HH:mm");
               } catch (error) {
                  return "Invalid date";
               }
            },
         },
         {
            accessorKey: "doc_upload_by",
            filter_name: "name",
            meta: { label: "Uploaded By" },
            header: () => <span>Upload By</span>,
            enableColumnFilter: true,
            isVisible: true,
         },
         {
            accessorKey: "doc_size",
            meta: { label: "Document Size" },
            header: () => <span>Document Size</span>,
            enableColumnFilter: false,
            isVisible: true,
            cell: ({ getValue }) => {
               const siz = getValue();
               return prettyBytes(siz);
            },
         },
         {
            accessorKey: "doc_status",
            filter_name: "status",
            meta: { label: "Document Status" },
            header: () => <span>Document Status</span>,
            Filter: ({ column }) => <SelectFilter column={{ ...column, value: columnFiltersRef.current[column.id] || "" }} constants={DOCUMENT_PROCESSING_STATUS} handleInputFilterChangeNoDebounce={handleInputFilterChangeNoDebounce} />,
            enableColumnFilter: true,
            isVisible: true,
            cell: ({ getValue }) => {
               const status = getValue();
               // Directly display the status from SQL since it's already formatted
               return status || "Unknown";
            },
         },
         {
            accessorKey: "action",
            header: () => <span>Action</span>,
            enableHiding: true,
            enableResizing: false,
            excludeFromDropdown: true,
            minSize: 125,
            maxSize: 125,
            width: 125,
            cell: (info) => (
               <div
                  style={{
                     whiteSpace: "nowrap",
                     overflow: "hidden",
                     textOverflow: "ellipsis",
                  }}>
                  {info.getValue()}
               </div>
            ),
         },
      ],
      []
   );
   const [pagination, setPagination] = useState({
      pageIndex: 0,
      // When deep-linking, fetch a larger page so the target doc is likely on page 1.
      pageSize: deepLinkDocId ? 100 : 10,
      totalPages: 0,
   });
   const nextPage = () => {
      setPagination((prev) => ({
         ...prev,
         pageIndex: Math.min(prev.pageIndex + 1, prev.totalPages - 1),
      }));
   };
   const previousPage = () => {
      setPagination((prev) => ({
         ...prev,
         pageIndex: Math.max(prev.pageIndex - 1, 0),
      }));
   };
   const [columnVisibility, setColumnVisibility] = useState(() => {
      return columns
         ? columns.reduce((acc, column) => {
              acc[column.accessorKey] = column.isVisible !== false; // Start with all columns visible
              return acc;
           }, {})
         : {};
   });
   const [showDropdown, setShowDropdown] = useState(false);
   const [tempPagination, setTempPagination] = useState(pagination);
   const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibility);
   // Log the columnVisibility to check its value
   useEffect(() => {
      console.log("Column Visibility:", tempColumnVisibility);
   }, [tempColumnVisibility]); // This will log whenever columnVisibility changes
   const handleApplyChanges = (size) => {
      // Apply temporary state to actual table settings
      setPagination((prev) => ({
         ...prev,
         pageSize: tempPagination.pageSize,
         pageIndex: 0, // restart at first page when page size changes
      }));

      setColumnVisibility(tempColumnVisibility);
      setShowDropdown(false); // Close dropdown on apply
   };
   const handleCancelChanges = () => {
      // Reset temporary state to match actual table settings
      setTempPagination(pagination);
      setTempColumnVisibility(columnVisibility);
      setShowDropdown(false); // Close dropdown on apply
   };
   const table = useReactTable({
      data: documents,
      columns,
      tableLayout: "fixed",
      pageCount: pagination.totalPages,
      manualPagination: true,
      manualFiltering: true,
      state: {
         columnVisibility,
      },
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });
   // Sync tempPagination with pagination when opening the dropdown
   const handleToggleDropdown = (isOpen) => {
      setShowDropdown(isOpen);
      if (isOpen) {
         // Sync with current pagination
         setTempPagination(pagination);
         setTempColumnVisibility(columnVisibility);
      }
   };
   const handlePageSizeChange = (size) => {
      setTempPagination({
         ...tempPagination,
         pageSize: size,
      });
   };
   const handleColumnVisibilityChange = (columnId) => {
      setTempColumnVisibility((prev) => ({
         ...prev,
         [columnId]: !prev[columnId],
      }));
   };
   const updateTotalPages = (totalPages) => {
      setPagination((prev) => {
         if (prev.totalPages === totalPages) {
            return prev; // No update = no re-render
         }
         return { ...prev, totalPages };
      });
   };
   const fetchDocuments = async (filters, signal) => {
      const url = "/getDocs.sz";
      const method = "POST";
      try {
         const { responseData, fetchError } = await fetchData(url, method, filters, "form-data", "json", signal);
         if (fetchError || !responseData) return;
         if (responseData.st === 1) {
            if (Array.isArray(responseData.payload)) {
               setDocuments(responseData.payload);
               updateTotalPages(responseData.total_pages);
            } else {
               console.error("Invalid data structure, payload is not an array");
            }
         } else {
            if (responseData.m_en) {
               setError(responseData.m_en);
            } else {
               setError("We seem to have an issue authenticating, please try again later.");
            }
         }
      } catch (error) {
         console.error("Error fetching data:", error);
      }
   };
   const fetchFilters = (pagination, columnFilters) => {
      const formData = new FormData();
      formData.append("_page_index", pagination.pageIndex || 0);
      formData.append("_page_size", pagination.pageSize || 10);
      if (columnFilters.doc_name) {
         formData.append("_doc_name", columnFilters.doc_name);
      }
      if (columnFilters.doc_type) {
         formData.append("_doc_type", columnFilters.doc_type);
      }
      if (columnFilters.doc_upload_by) {
         formData.append("_doc_upload_by", columnFilters.doc_upload_by);
      }
      if (columnFilters.doc_status) {
         formData.append("_doc_status", columnFilters.doc_status);
      }
      if (columnFilters.doc_status_date?.startDate) {
         formData.append("_doc_status_startdate", format(columnFilters.doc_status_date.startDate, "yyyy-MM-dd"));
      }
      if (columnFilters.doc_status_date?.endDate) {
         formData.append("_doc_status_enddate", format(columnFilters.doc_status_date.endDate, "yyyy-MM-dd"));
      }
      return formData;
   };

   useEffect(() => {
      return () => {
         if (abortControllerRef.current) abortControllerRef.current.abort();
      };
   }, []);

   useEffect(() => {
      const prev = prevFetchParamsRef.current;
      if (
         prev &&
         prev.pageIndex === pagination.pageIndex &&
         prev.pageSize === pagination.pageSize &&
         prev.columnFilters === columnFilters
      ) {
         return;
      }
      prevFetchParamsRef.current = {
         pageIndex: pagination.pageIndex,
         pageSize: pagination.pageSize,
         columnFilters,
      };

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const formData = fetchFilters(pagination, columnFilters);
      fetchDocuments(formData, controller.signal);
   }, [pagination, columnFilters]);

   const [tempInputValues, setTempInputValues] = useState({});

   const debouncedFilterChange = useMemo(
      () =>
         debounce((columnId, value) => {
            setColumnFilters((prev) => ({
               ...prev,
               [columnId]: value,
            }));

            // Reset to first page
            setPagination((prev) => ({
               ...prev,
               pageIndex: 0,
            }));

            console.log(`Filtering for column ${columnId} with value: ${value}`);
         }, 300), // Reduced from 500ms to 300ms for faster response
      []
   );
   const handleInputFilterChangeNoDebounce = (columnId, value) => {
      setColumnFilters((prev) => {
         console.log("Previous Filters:", prev); // Log previous filters (prev state)
         console.log("Column ID:", columnId); // Log the columnId being passed
         console.log("Value:", value); // Log the value being passed
         const next = { ...prev, [columnId]: value };
         return next;
      });

      setPagination((prev) => ({
         ...prev,
         pageIndex: 0, // Reset to first page when filter changes
      }));
   };
   const handleInputFilterChange = (columnId, value) => {
      // Update temp input for immediate visual feedback
      setTempInputValues((prev) => ({
         ...prev,
         [columnId]: value,
      }));
      // Debounce the actual filter update
      debouncedFilterChange(columnId, value);
   };
   // Cleanup debounce on component unmount
   useEffect(() => {
      return () => {
         debouncedFilterChange.cancel();
      };
   }, [debouncedFilterChange]);

   // Deep-link: once documents are loaded, open the target doc if ?doc= is present.
   // Runs only once per mount (guarded by ref so it doesn't refire on subsequent fetches).
   useEffect(() => {
      if (!deepLinkDocId || deepLinkTriggeredRef.current) return;
      if (documents.length === 0) return;
      // getDocs.sz aliases `d.doc_external_id AS doc_id`, so the docs-list `doc_id`
      // field already holds the external_id UUID that arrives in `?doc=<uuid>`.
      const target = documents.find((d) => d.doc_id === deepLinkDocId);
      if (target) {
         deepLinkTriggeredRef.current = true;
         handleOpenDocumentDetail(target.doc_id);
      }
      // If not found in this page, do nothing — user lands on the unfiltered list.
   }, [documents, deepLinkDocId]);

   const handleDocumentModalClose = () => {
      setTimeout(() => {
         setShowDocumentModal(null);
         setShowDetailModal(false);
      }, 300);
   };

   // Handler for opening DocumentModal when clicking document name
   const handleOpenDocumentDetail = (docIdValue) => {
      setDocId(docIdValue);
      setShowDetailModal(true);
   };

   // this listens for the "View" button
   const handleViewPdf = async ({ row }) => {
      if (row.original) {
         const docObj = {
            id: row.original.doc_id,
            name: row.original.doc_name,
            uploadOn: row.original.doc_status_date,
            uploadBy: row.original.doc_upload_by,
         };
         setDocObj(docObj);
         await fetchPdfContent({ docObj });
         setShowDocumentModal(true);
      }
   };

   const fetchPdfContent = async ({ docObj }) => {
      if (!docObj) return;
      setIsLoading(true);

      const url = "/getfile.sz";
      const method = "POST";
      const formData = new FormData();
      formData.append("_doc_id", docObj.id);
      try {
         const { responseData, fetchError } = await fetchData(url, method, formData, "form-data", "blob");
         if (fetchError) {
            triggerToast("error", "Failed to load PDF. Please try again.", true, "Error", false, 0);
            return;
         }
         const base64Data = await convertBlobToBase64(responseData);
         setImageData(base64Data);
      } catch (error) {
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      } finally {
         setIsLoading(false);
      }
   };

   // Helper function to convert Blob to Base64
   const convertBlobToBase64 = (blob) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.readAsDataURL(blob);
         reader.onloadend = () => resolve(reader.result.split(",")[1]);
         reader.onerror = reject;
      });
   };

   const handleClearFilters = () => {
      setColumnFilters({ doc_status_date: defaultDateRange, doc_name: "", doc_type: "", doc_upload_by: "", doc_status: "" });
      setTempInputValues({});
      setPagination((prev) => ({
         ...prev,
         pageIndex: 0,
      }));
   };

   const handleDeleteDocument = async (docId) => {
      if (!window.confirm("Are you sure you want to delete this document?")) {
         return;
      }
      
      try {
         const url = "/deleteDoc.sz";
         const method = "POST";
         const formData = new FormData();
         formData.append("_doc_id", docId);
         
         const { responseData, fetchError } = await fetchData(url, method, formData, "form-data");
         
         if (fetchError || responseData.st !== 1) {
            triggerToast("error", "Failed to delete document. Please try again.", true, "Error", false, 0);
            return;
         }
         
         triggerToast("success", "Document deleted successfully.", true, "Success", false, 0);
         // Refresh the document list
         const formDataFetch = fetchFilters(pagination, columnFilters);
         fetchDocuments(formDataFetch);
      } catch (error) {
         console.error("Error deleting document:", error);
         triggerToast("error", "Network issue, please try again.", true, "Network Issue", false, 0);
      }
   };

   return (
      <>
         <style>{styles}</style>
         <Header />
         <div className="main main-app p-3 p-lg-4 p-xxl-5">
            <Row className="g-3 g-lg-4">
               <Col>
                  <h2 className="main-title fs-4 mb-1">Documents</h2>
                  <p className="text-secondary mb-4"></p>
               </Col>
            </Row>
            <Row className="pt-4 pb-2">
               <Col>
                  <button type="button" onClick={handleClearFilters} className="btn btn-outline-secondary rounded-pill pt-1 pb-1">
                     Clear Filters
                  </button>
               </Col>
            </Row>
            <div className="data-table-card">
            <Row className="g-3 g-lg-4">
               <Col>
                  <div className="position-relative d-flex mb-2">
                     <table className="table">
                        <thead>
                           {table.getHeaderGroups().map((headerGroup) => (
                              <tr key={headerGroup.id}>
                                 {headerGroup.headers.map((header) => {
                                    if (columnVisibility[header.id] === false) {
                                       return null;
                                    }
                                    if (header.id.toLowerCase() === "doc_upload_date" || header.id.toLowerCase() === "doc_status_date" || header.id.toLowerCase() === "doc_type" || header.id.toLowerCase() === "doc_status") {
                                       return (
                                          <th key={header.id}>
                                             {flexRender(header.column.columnDef.header, header.getContext())}
                                             {header.column.columnDef.enableColumnFilter && header.column.columnDef.Filter && flexRender(header.column.columnDef.Filter, header.getContext())}
                                          </th>
                                       );
                                    } else if (header.id.toLowerCase() === "action") {
                                       return (
                                          <th
                                             key={header.id}
                                             style={{
                                                maxWidth: header.column.columnDef.maxSize || "auto",
                                                minWidth: header.column.columnDef.minSize || "auto",
                                                width: header.column.columnDef.size || "auto",
                                             }}>
                                             <div className="d-flex flex-column align-items-end">
                                                <div className="d-flex align-items-center">
                                                   <div className="me-4">{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                                   <span>
                                                      <TableSettingDropdown showDropdown={showDropdown} handleToggleDropdown={handleToggleDropdown} tempPagination={tempPagination} handlePageSizeChange={handlePageSizeChange} table={table} tempColumnVisibility={tempColumnVisibility} handleColumnVisibilityChange={handleColumnVisibilityChange} handleApplyChanges={handleApplyChanges} handleCancelChanges={handleCancelChanges} />
                                                   </span>
                                                </div>
                                                <div className="w-100">
                                                   <input className="form-control mt-2 hidden-input" type="text" />
                                                </div>
                                             </div>
                                          </th>
                                       );
                                    } else {
                                       return (
                                          <th key={header.id}>
                                             {flexRender(header.column.columnDef.header, header.getContext())}
                                             {header.column.columnDef.enableColumnFilter ? (
                                                <input
                                                   className="form-control mt-2"
                                                   type="text"
                                                   placeholder={`Search ${header.column.columnDef.filter_name || header.id}`}
                                                   value={(tempInputValues[header.column.id] ?? columnFilters[header.column.id]) || ""}
                                                   onChange={(e) => handleInputFilterChange(header.column.id, e.target.value)}
                                                />
                                             ) : (
                                                <input className="form-control mt-2 hidden-input" type="text" />
                                             )}
                                          </th>
                                       );
                                    }
                                 })}
                              </tr>
                           ))}
                        </thead>
                        <tbody>
                           {documents?.length === 0 ? (
                              <tr key="tr-1">
                                 <td colSpan={columns.length}>No Documents found...</td>
                              </tr>
                           ) : (
                              table.getRowModel().rows.map((row) => (
                                 <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                       if (cell.column.id.toLowerCase() === "action") {
                                          return (
                                             <td
                                                key={cell.id}
                                                style={{
                                                   maxWidth: cell.column.columnDef.maxSize || "auto",
                                                   minWidth: cell.column.columnDef.minSize || "auto",
                                                   width: cell.column.columnDef.size || "auto",
                                                }}>
                                                <div
                                                   style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "flex-end",
                                                      gap: "8px", // Spacing between elements
                                                   }}>
                                                   <a href="#" onClick={(e) => { e.preventDefault(); handleViewPdf({ row }); }}>
                                                      View
                                                   </a>
                                                   <div
                                                      style={{
                                                         borderLeft: "1px solid #ccc",
                                                         height: "16px",
                                                      }}></div>
                                                   <div className="dropdown">
                                                      <button className="btn btn-link" type="button" id={`dropdown-${row.id}`} data-bs-toggle="dropdown" aria-expanded="false" style={{ padding: 0, border: "none", cursor: "pointer" }}>
                                                         <FaChevronDown />
                                                      </button>
                                                      <ul className="dropdown-menu" aria-labelledby={`dropdown-${row.id}`}>
                                                         <li>
                                                            <button className="dropdown-item" onClick={() => handleDeleteDocument(row.original.doc_id)}>
                                                               Delete
                                                            </button>
                                                         </li>
                                                      </ul>
                                                   </div>
                                                </div>
                                             </td>
                                          );
                                       } else {
                                          return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                                       }
                                    })}
                                 </tr>
                              ))
                           )}
                        </tbody>
                     </table>
                  </div>
               </Col>
            </Row>
            <Row className="g-3 g-lg-4">
               <Col>
                  {documents?.length === 0 ? null : (
                     <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex justify-content-center flex-grow-1">
                           <span>
                              Page <strong>{pagination.pageIndex + 1 || 1}</strong> of {pagination.totalPages || 1}
                           </span>
                        </div>

                        <div className="d-flex justify-content-end">
                           <button className="btn btn-primary me-2" onClick={previousPage} disabled={pagination.pageIndex === 0}>
                              Previous
                           </button>
                           <button className="btn btn-primary" onClick={nextPage} disabled={pagination.pageIndex >= pagination.totalPages - 1}>
                              Next
                           </button>
                        </div>
                     </div>
                  )}
               </Col>
            </Row>
            </div>
            {showDocumentModal && <PdfModal imageData={imageData} propScale={1.0} docObj={docObj} onCloseModal={handleDocumentModalClose} />}
            {showDetailModal && <DocumentModal docId={docId} onCloseModal={handleDocumentModalClose} />}
            <Footer />
         </div>
      </>
   );
}
